import { useState } from "preact/hooks";
import { locale, tr } from "../i18n.js";
import { fmtMoney, get, html, post, put } from "../lib.js";
import { Badge, Field, Modal, useAction, useForm, useLoad } from "../ui.js";

const STATUS = {
  pending_payment: ["warn", "Awaiting payment"],
  active: ["ok", "Active"],
  used_up: ["muted", "Used up"],
  expired: ["muted", "Expired"],
};
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(locale(), { day: "numeric", month: "short", year: "numeric" }) : "—");

/**
 * Packages and punch cards: sessions sold up front. The agent sells them with a payment link when
 * Grow is connected; staff record a sale made at the desk here. Bookings then use the credit.
 */
export function PackagesCard({ t, services }) {
  const [data, reload] = useLoad(() => get(`${t.base}/packages`), [t.id]);
  const [editing, setEditing] = useState(null);
  const [selling, setSelling] = useState(null);
  if (!data) return null;
  const canEdit = t.role !== "agent";
  const serviceName = (id) => services.find((s) => s.id === id)?.name ?? id;
  return html`<section class="card">
    <div class="section-head"><h2>${tr("Packages and punch cards")}</h2>${canEdit && html`<button class="btn" onClick=${() => setEditing({})}>${tr("Add package")}</button>`}</div>
    <p class="muted small">${tr("Sessions paid up front, such as 10 classes or a course of 6 treatments. The agent offers them and sends a payment link when Grow is connected; a sale you make at the desk is recorded here. Bookings then use the sessions automatically, and a cancelled booking gives its session back.")}</p>
    ${data.packages.length > 0 && html`<table class="table"><tbody>${data.packages.map((p) => html`<tr class=${p.active ? "" : "dim"}>
      <td><strong dir="auto">${p.name}</strong>${!p.active && html` <${Badge} kind="muted">${tr("hidden")}</${Badge}>`}
        <div class="muted small">${tr("{n} sessions", { n: p.sessions })} · ${p.service_ids.length ? p.service_ids.map(serviceName).join(", ") : tr("any service")}${p.valid_days ? ` · ${tr("valid {n} days", { n: p.valid_days })}` : ""}</div></td>
      <td>${fmtMoney(p.price, p.currency)}</td>
      <td class="small muted">${tr("{n} sold", { n: p.sold })}</td>
      <td class="row-actions">${canEdit && html`<button class="btn small primary" disabled=${!p.active} onClick=${() => setSelling(p)}>${tr("Record a sale")}</button><button class="btn small ghost" onClick=${() => setEditing(p)}>${tr("Edit")}</button>`}</td>
    </tr>`)}</tbody></table>`}
    ${data.sales.length > 0 && html`<h3>${tr("Recent sales")}</h3>
      <table class="table"><tbody>${data.sales.slice(0, 20).map((s) => html`<tr>
        <td><span dir="auto">${s.contact_name ?? s.contact_phone ?? s.payer_name ?? "—"}</span><div class="muted small" dir="auto">${s.name}</div></td>
        <td class="small">${tr("{used} of {total} used", { used: s.sessions_used, total: s.sessions_total })}</td>
        <td><${Badge} kind=${STATUS[s.status]?.[0] ?? "neutral"}>${tr(STATUS[s.status]?.[1] ?? s.status)}</${Badge}></td>
        <td class="small muted">${s.expires_at ? tr("until {date}", { date: day(s.expires_at) }) : s.source === "staff" ? tr("at the desk") : tr("online")}</td>
      </tr>`)}</tbody></table>`}
    ${editing && html`<${PackageForm} t=${t} pkg=${editing} services=${services} onClose=${() => setEditing(null)} onSaved=${() => { setEditing(null); reload(); }} />`}
    ${selling && html`<${SaleForm} t=${t} pkg=${selling} onClose=${() => setSelling(null)} onSaved=${() => { setSelling(null); reload(); }} />`}
  </section>`;
}

function PackageForm({ t, pkg, services, onClose, onSaved }) {
  const isNew = !pkg.id;
  const { values, bind, check, setValues } = useForm({
    name: pkg.name ?? "",
    description: pkg.description ?? "",
    sessions: pkg.sessions ?? 10,
    price: pkg.price ?? "",
    currency: pkg.currency ?? services[0]?.currency ?? "ILS",
    valid_days: pkg.valid_days ?? "",
    active: pkg.active ?? true,
    service_ids: pkg.service_ids ?? [],
  });
  const [busy, run] = useAction();
  const toggleService = (id) => setValues((v) => ({ ...v, service_ids: v.service_ids.includes(id) ? v.service_ids.filter((x) => x !== id) : [...v.service_ids, id] }));
  const save = async (e) => {
    e.preventDefault();
    const payload = { ...values, sessions: Number(values.sessions), price: Number(values.price), valid_days: values.valid_days ? Number(values.valid_days) : null };
    await run(() => (isNew ? post(`${t.base}/packages`, payload) : put(`${t.base}/packages/${pkg.id}`, payload)), tr("Package saved"));
    onSaved();
  };
  return html`<${Modal} title=${isNew ? tr("Add package") : tr("Edit package")} onClose=${onClose}>
    <form onSubmit=${save}>
      <${Field} label=${tr("Name")} hint=${tr("What customers see, e.g. 10 pilates classes")}><input required maxlength="100" dir="auto" ...${bind("name")} /></${Field}>
      <div class="row">
        <${Field} label=${tr("Sessions")}><input required type="number" min="1" max="200" ...${bind("sessions")} /></${Field}>
        <${Field} label=${tr("Price")}><input required type="number" min="0" step="1" ...${bind("price")} /></${Field}>
        <${Field} label=${tr("Valid for (days)")} hint=${tr("Empty for no expiry")}><input type="number" min="1" ...${bind("valid_days")} /></${Field}>
      </div>
      <h3>${tr("Can be used for")}</h3>
      <div class="chips">${services.map((s) => html`<button type="button" class=${values.service_ids.includes(s.id) ? "chip active" : "chip"} onClick=${() => toggleService(s.id)}>${s.name}</button>`)}</div>
      <p class="muted small">${values.service_ids.length ? "" : tr("None chosen: any service.")}</p>
      <${Field} label=${tr("Description")} hint=${tr("Optional: what's included, for the agent to explain")}><textarea rows="2" dir="auto" ...${bind("description")}></textarea></${Field}>
      <label class="toggle"><input type="checkbox" ...${check("active")} /> ${tr("Offered to customers")}</label>
      ${!isNew && html`<p class="muted small">${tr("Changes apply to new sales. Customers who already bought keep what they paid for.")}</p>`}
      <div class="form-actions"><button class="btn primary" disabled=${busy}>${tr("Save")}</button><button type="button" class="btn ghost" onClick=${onClose}>${tr("Cancel")}</button></div>
    </form>
  </${Modal}>`;
}

function SaleForm({ t, pkg, onClose, onSaved }) {
  const { values, bind } = useForm({ phone: "", name: "" });
  const [busy, run] = useAction();
  const save = async (e) => {
    e.preventDefault();
    await run(() => post(`${t.base}/packages/sales`, { package_id: pkg.id, phone: values.phone, name: values.name || undefined }), tr("Sale recorded"));
    onSaved();
  };
  return html`<${Modal} title=${tr("Record a sale: {name}", { name: pkg.name })} onClose=${onClose}>
    <form onSubmit=${save}>
      <p class="muted small">${tr("For a package paid at the desk (cash, card terminal or transfer). The sessions are available to book right away.")}</p>
      <${Field} label=${tr("Customer's phone")}><input required dir="ltr" inputmode="tel" placeholder="050-123-4567" ...${bind("phone")} /></${Field}>
      <${Field} label=${tr("Customer's name")} hint=${tr("Used if this is a new customer")}><input maxlength="100" dir="auto" ...${bind("name")} /></${Field}>
      <div class="form-actions"><button class="btn primary" disabled=${busy}>${tr("Record sale of {price}", { price: fmtMoney(pkg.price, pkg.currency) })}</button><button type="button" class="btn ghost" onClick=${onClose}>${tr("Cancel")}</button></div>
    </form>
  </${Modal}>`;
}
